import { User, Users, PawPrint, Camera } from "lucide-react";
import { Reveal, Eyebrow } from "./Reveal";

const CARDS = [
  {
    id: "portraits",
    icon: User,
    title: "Portraits",
    tagline: "Single & Double Studies",
    description: "Capturing individual character, expressions, and intimate personal likeness in oil or acrylic.",
  },
  {
    id: "family",
    icon: Users,
    title: "Couple / Family",
    tagline: "Cherished Milestones",
    description: "Timeless compositions celebrating shared bonds, wedding milestones, and generational roots.",
  },
  {
    id: "pets",
    icon: PawPrint,
    title: "Pet Portraits",
    tagline: "Beloved Companions",
    description: "Celebrating cherished companion animals with warmth, distinctive traits, and lively eyes.",
  },
  {
    id: "memories",
    icon: Camera,
    title: "Memory Paintings",
    tagline: "Nostalgic Moments",
    description: "Turning old photographs, scenic travels, and nostalgic memories into permanent fine art.",
  },
];

export default function Commissions() {
  return (
    <section id="commissions" className="py-24 sm:py-32">
      <div className="mx-auto max-w-7xl px-6 lg:px-10">
        <Reveal>
          <Eyebrow>Custom Artworks</Eyebrow>
          <div className="mt-6 flex flex-col justify-between gap-6 md:flex-row md:items-end">
            <h2 className="max-w-xl font-serif text-3xl tracking-tight text-ink sm:text-5xl lg:text-6xl">
              Commissioned Artwork
            </h2>
            <p className="font-serif text-lg italic text-clay sm:text-xl">
              Tailored for your personal stories &amp; spaces
            </p>
          </div>
        </Reveal>

        <div className="mt-16 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {CARDS.map((card, i) => (
            <Reveal key={card.id} delay={i * 0.08} className="h-full">
              <div
                data-testid={`commission-card-${card.id}`}
                className="group flex h-full flex-col border border-linen bg-white p-8 transition-all duration-500 hover:-translate-y-2 hover:border-gild/60 hover:shadow-[0_30px_50px_-24px_rgba(24,19,16,0.28)]"
              >
                <span className="inline-flex h-12 w-12 items-center justify-center rounded-full border border-linen text-vermilion transition-colors duration-500 group-hover:border-vermilion group-hover:bg-vermilion group-hover:text-cream">
                  <card.icon className="h-5 w-5" />
                </span>
                <h3 className="mt-8 font-serif text-2xl tracking-tight text-ink">{card.title}</h3>
                <p className="mt-2 font-mono text-[10px] uppercase tracking-[0.24em] text-gild">
                  {card.tagline}
                </p>
                <p className="mt-5 text-sm leading-relaxed text-clay sm:text-base">
                  {card.description}
                </p>
                <span className="mt-auto block h-px w-full bg-linen pt-0">
                  <span className="mt-0 block h-px w-0 bg-vermilion transition-all duration-500 group-hover:w-full" />
                </span>
              </div>
            </Reveal>
          ))}
        </div>

        <Reveal delay={0.1}>
          <div className="mt-24 border-t border-linen pt-14" data-testid="how-it-works">
            <div className="flex flex-col justify-between gap-4 md:flex-row md:items-end">
              <div>
                <Eyebrow>How It Works</Eyebrow>
                <h3 className="mt-5 font-serif text-2xl tracking-tight text-ink sm:text-4xl">
                  Commissioning is simple.
                </h3>
              </div>
              <p className="max-w-sm text-sm leading-relaxed text-clay sm:text-base">
                Four friendly steps from your first message to a finished, hand-painted original.
              </p>
            </div>

            <div className="mt-10 grid grid-cols-1 gap-8 sm:grid-cols-2 lg:grid-cols-4">
              {[
                [
                  "Share your idea",
                  "Send a photo, a memory, or just a thought — we talk about the size, the medium, and the feeling you want.",
                ],
                [
                  "Sketch & confirm",
                  "I prepare the composition and we agree on every detail before painting begins.",
                ],
                [
                  "Hand-painted",
                  "Your piece is painted by hand in oil or acrylic, with patient layering, texture, and detail.",
                ],
                [
                  "Treasured forever",
                  "Your finished original arrives ready to frame, keep, or gift.",
                ],
              ].map(([title, text], i) => (
                <Reveal key={title} delay={i * 0.08} className="h-full">
                  <div
                    data-testid={`commission-step-${i + 1}`}
                    className="h-full border-l border-linen pl-5 transition-colors duration-500 hover:border-vermilion"
                  >
                    <p className="font-serif text-3xl italic text-gild/70">
                      {String(i + 1).padStart(2, "0")}
                    </p>
                    <h4 className="mt-4 font-serif text-xl text-ink">{title}</h4>
                    <p className="mt-2 text-sm leading-relaxed text-clay">{text}</p>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
}
